import AegisV2 from '@tencent/aegis-web-sdk-v2';
import { AxiosError, AxiosResponse } from '@tencent/oneid-request';
import { TApiError } from 'common/apiRequest/Request';
import message from 'common/components/Message';
import {
  ERROR_MSG,
  getUnknownMistake,
  NO_COMPANY_ERROR,
  NO_PERMISSION,
} from 'common/constants/Constants';
import i18n from 'common/locales';
import Utils from 'common/utils';

type TError = { errCode: string; errMessage: string; [key: string]: any };

/**
 * @param response 请求成功拦截器
 * @returns response.data
 */
const onSuccess = (response: AxiosResponse): Promise<any> => {
  const data = response.data || {};
  handleBusinessArr({ ...response, ...data });

  window.aegis?.report({
    type: 'api',
    level: AegisV2.logLevel.INFO,
    msg: Utils.aegisSuccessInfo(response),
    status: response.status,
  });

  return Promise.resolve(data);
};

/**
 * @param error 请求失败，状态码非200
 * @returns Promise.resolve(err)
 */
const onError = (error: TApiError) => {
  const err = error.response || { status: 0 };

  if (err?.status >= 500) {
    window.aegis?.report({
      type: 'api',
      level: AegisV2.logLevel.AJAX_ERROR,
      msg: Utils.aegisErrorInfo(error),
      status: err.status,
    });
  }

  return handleError(error);
};

/**
 * 转换后端返回的错误格式成通用格式
 * @param err 错误对象
 * @param errCodeKey 错误码key
 * @param errMsgKey 错误消息key
 */
export const formatApiError = (err: TError, errCodeKey: string, errMsgKey: string) => {
  return { ...err, errCode: err?.[errCodeKey], errMessage: err?.[errMsgKey] };
};

const handleError = (error: TApiError) => {
  let errData = _.get(error.response || {}, 'data') as TError;
  // 兼容 error、error_description 错误格式
  if (errData?.error) {
    errData = formatApiError(errData, 'error', 'error_description');
  }
  // 网关报错时没有 errCode 字段, 使用assign，保持AxiosError类型不变，便于下游判断
  const err = Object.assign(
    error,
    errData?.errCode
      ? errData
      : {
          status: Number(error.status || 0),
          errCode: error.code || '',
          errMessage: error.message || getUnknownMistake(),
        },
  );
  const status = Number(_.get(err, 'status'));

  // 重复请求自动取消，无需处理
  if (_.get(error, 'code') === 'ERR_CANCELED') {
    return Promise.resolve(err);
  }

  // 处理401，403，412特殊逻辑
  switch (status) {
    case 401: {
      Utils.handleNoAuth();
      return Promise.resolve(err);
    }
    case 403: {
      handleForbidden(err);
      return Promise.resolve(err);
    }
    case 412: {
      handleNoCompany(NO_COMPANY_ERROR);
      return Promise.resolve(err);
    }
    default: {
      return handleBusinessArr(err);
    }
  }
};

// 412 自定义域名下，域名不可用则跳转到当前自定义域名的 IDP 错误页
const handleNoCompany = (errCode: string) => {
  Utils.redirectToIdp(undefined, `/sso_login/error?errCode=${errCode}`);
};

// 403 admin模块 二次验证过期，其他模块不处理
const handleForbidden = async (error: TError) => {
  const errCode = _.get(error, 'errCode');
  // 错误码命中G40301时，没有相关权限，需要特殊报错
  if (errCode === NO_PERMISSION && !_.get(error, 'config.skipShowError')) {
    message.error(i18n.t('暂无权限'));
    return;
  }
  if (RUNTIME_APP_NAME === 'admin' && errCode === 'E0010394') {
    window.location.reload();
  }
  const agc = _.get(error, 'data.errMessage');
  // 其他用户采用新逻辑。get请求，即获取数据的方法不抛出错误消息
  if (error?.config?.method !== 'get' && agc) {
    const content = await Utils.getPermsErrMsgByCode(agc);
    message.error({ content });
  }
};

// 处理业务报错，状态为200
const handleBusinessArr = (error: TError) => {
  const errorCode = error?.errCode;
  // 跳过报错或请求被取消
  if (_.get(error, 'config.skipShowError') || errorCode === AxiosError.ERR_CANCELED) {
    return Promise.resolve(error);
  }
  // 根据errorCode统一报错
  if (ERROR_MSG.has(errorCode)) {
    const msg = ERROR_MSG.get(errorCode);
    msg && message.error({ content: msg });
    return Promise.resolve(error);
  }

  const errorMsg = _.get(error, 'errMessage');

  if (errorMsg) {
    message.error({ content: errorMsg });
  }
  return Promise.resolve(error);
};

export { onSuccess, onError };
